import {
  Avatar,
  Box,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { getPharmacyImage } from "../../utils/utils";
import { useCallback, useEffect, useRef } from "react";
import { StyledTableCell } from "../common/styles";
import { ExpansePDFHeader } from "../../utils/headerConstant";
import AppButton from "../common/AppButton";
import { useReactToPrint } from "react-to-print";
import { getProfileData } from "../../features/profile/profileSlice";

const DailyExpenseReport = ({
  handleClosePdf,
  expenseData,
  formState,
}: any) => {

  const totalAmount = expenseData?.reduce(
    (total: any, item: any) => total + item.amount,
    0
  );
  const pharmacyId = localStorage?.getItem("pharmacyId");
  const dispatch = useAppDispatch();
  const componentRef = useRef<HTMLDivElement | null>(null);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });
  const profiledata: any = useAppSelector(
    (state: any) => state.profile.profileDetails
  );

  const getProfile = useCallback(() => {
    dispatch(getProfileData(pharmacyId));
  }, [dispatch]);

  useEffect(() => {
    getProfile();
  }, [getProfile]);

  const formatDate = (dateString: any) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB"); // Formats as DD/MM/YYYY
  };

  return (
    <>
      <style>
        {`
      @media print {
        .table-width {
          max-height: none !important;
          overflow: visible !important;
        }
  
        .MuiTableContainer-root {
          max-height: none !important;
          overflow: visible !important;
        }
  
        .print-footer {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          width: 100%;
          font-size: 12px;
          border-top: 1px solid #DDDDDD;
          background: white;
          padding: 8px 32px;
          display: flex;
          justify-content: center;
          align-items: center;
        }
      }
  
      @media screen {
        .print-footer {
          display: none;
        }
      }
    `}
      </style>

      <Box
        ref={componentRef}
        sx={
          {
            //   display: "flex",
            //   flexDirection: "column",
          }
        }
      >
        {/* Header Section */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "10px",
            pl: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "5px",
            }}
          >
            <Box sx={{ fontWeight: "600", fontSize: "20px", color: "#005DB0" }}>
              {profiledata?.pharmacy?.name ?? "-"}
            </Box>

            <Box sx={{ fontWeight: "500", fontSize: "13px" }}>
              {profiledata?.pharmacy?.address?.address ?? "-"}
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: "5px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box sx={{ fontWeight: "500", fontSize: "12px" }}>
                  Ph:{""}
                  {profiledata?.pharmacy?.phone_number
                    ? profiledata?.pharmacy?.phone_number
                    : "-"}
                </Box>

                <Box>
                  {formState.fromDate && formState.toDate ? (
                    <Box sx={{ fontWeight: "500", fontSize: "12px" }}>
                      Date:{" "}
                      <Box
                        component="span"
                        sx={{ fontWeight: "500", fontSize: "12px" }}
                      >
                        {`${formatDate(formState?.fromDate)} - ${formatDate(
                          formState?.toDate
                        )}`}
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ fontWeight: "500", fontSize: "12px" }}>
                      Date:{" "}
                      {formState?.selectedDate === "" ? (
                        new Date().toLocaleDateString("en-GB")
                      ) : (
                        <Box
                          component="span"
                          sx={{ fontWeight: "500", fontSize: "12px" }}
                        >
                          {formState?.selectedDate
                            ? formatDate(formState?.selectedDate)
                            : "-"}
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

          <Avatar
            src={getPharmacyImage(profiledata?.pharmacy?.id)}
            sx={{ width: 140, height: 130 }}
          />
        </Box>

        {/* border */}
        <Box
          sx={{
            borderTop: "6px double #c6c6c6",
          }}
        >
          {/* <BorderPdfIcon /> */}
        </Box>

        {/** Daily Expense Report Title */}

        <Box
          sx={{
            my: 1,
            px: 2,
            fontWeight: "500",
            fontSize: "15px",
          }}
        >
          EXPENSE INFORMATION
        </Box>

        {/** Daily Expense Report Table */}

        <TableContainer
          className="no-print-scroll"
          sx={{
            maxHeight: 500,
            overflow: "auto",
          }}
        >
          <Box className="table-width">
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {ExpansePDFHeader?.map((data: any, index: any) => (
                    <StyledTableCell
                      key={index}
                      align={data === "Actions" ? "right" : undefined}
                    >
                      {data}
                    </StyledTableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                <>
                  {expenseData?.map((data: any) => (
                    <TableRow>
                      <StyledTableCell>{data?.name ?? "-"}</StyledTableCell>
                      <StyledTableCell>{`Rs. ${
                        data?.amount ?? "-"
                      }`}</StyledTableCell>
                      <StyledTableCell>
                        {data?.exp_category?.name ?? "-"}
                      </StyledTableCell>
                      <StyledTableCell>
                        {formatDate(data?.expense_date)}
                      </StyledTableCell>
                      <StyledTableCell>
                        {formatDate(data?.created_at)}
                      </StyledTableCell>
                      <StyledTableCell>
                        {formatDate(data?.updated_at)}
                      </StyledTableCell>
                    </TableRow>
                  ))}
                </>
              </TableBody>
            </Table>

            <Box>
              {/* Footer */}
              <Box sx={{ mt: 1, textAlign: "right", px: 3 }}>
                <Typography variant="subtitle1">
                  <strong>Total Number of Expense:</strong>{" "}
                  {expenseData?.length ?? "-"}
                </Typography>
              </Box>
              <Box sx={{ mt: 0, textAlign: "right", px: 3 }}>
                <Typography variant="subtitle1">
                  <strong>Total Amount of Expense:</strong> Rs.{" "}
                  {totalAmount ?? "-"}
                </Typography>
              </Box>

              {/* Footer Note */}
              {/* <Box
                  sx={{
                    mt: 2,
                    textAlign: "center",
                    borderTop: "1px solid #DDDDDD",
                    p: 2,
                    fontSize: "12px",
                  }}
                >
                  Software By E-Mareez Care, Phone# 03095558054
                </Box> */}
              <Box className="print-footer">
                <span>Software By E-Mareez Care, Phone# 03095558054</span>
                {/* <span className="print-page-number" /> */}
              </Box>
            </Box>
          </Box>
        </TableContainer>
      </Box>
      <Box sx={{ textAlign: "center", pb: 1 }} className="no-print">
        <AppButton
          variant="contained"
          color="primary"
          title="Print"
          sx={{ backgroundColor: "#01A768" }}
          onClick={() => {
            handlePrint();
            handleClosePdf();
          }}
        />
      </Box>
    </>
  );
};

export default DailyExpenseReport;
